import {
	useQuery,
	useInfiniteQuery,
	InfiniteData,
	QueryKey,
} from '@tanstack/react-query'

import { chatKeys } from '../../keys'
import chatService from '../services'

import type { operations } from '@/schema'
import { ChatMessageListResponse } from '../../sse'

type GetMessagesParams =
	operations['ChatController_getChatMessages']['parameters']['query']

export function useQueryChatMessages(
	roomId: string,
	params?: GetMessagesParams,
	enabled = true,
) {
	return useQuery({
		queryKey: chatKeys.messages(roomId, params),
		queryFn: () => chatService.getMessages(roomId, params),
		enabled: enabled && !!roomId,
	})
}

export function useInfiniteChatMessages(
	roomId: string,
	startCursor?: string | null,
	limit = 30,
) {
	const query = useInfiniteQuery<
		ChatMessageListResponse,
		Error,
		InfiniteData<ChatMessageListResponse>,
		QueryKey,
		| { cursor: string | undefined; direction: 'forward' | 'backward' }
		| undefined
	>({
		// Include startCursor in query key so changing it triggers a refetch
		queryKey: chatKeys.messages(roomId, { infinite: true, limit, startCursor }),
		queryFn: ({ pageParam }) => {
			// For initial fetch, use startCursor if provided (for jumping to specific message)
			// Otherwise use pageParam for pagination
			if (!pageParam && startCursor) {
				return chatService.getMessages(roomId, {
					limit,
					start: startCursor,
				})
			}

			return chatService.getMessages(roomId, {
				limit,
				...(pageParam?.direction === 'backward'
					? { start: pageParam.cursor }
					: pageParam?.direction === 'forward'
						? { end: pageParam.cursor }
						: {}),
			})
		},
		initialPageParam: undefined as
			| { cursor: string; direction: 'forward' | 'backward' }
			| undefined,
		getNextPageParam: (lastPage) =>
			lastPage.pagination.hasMore
				? {
						cursor: lastPage.pagination.nextCursor,
						direction: 'backward' as const,
					}
				: undefined,
		getPreviousPageParam: (firstPage) =>
			firstPage.pagination.prevCursor
				? {
						cursor: firstPage.pagination.prevCursor,
						direction: 'forward' as const,
					}
				: undefined,
		enabled: !!roomId,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		staleTime: 0,
		gcTime: 0,
	})

	return query
}
